import axiosInstance from '../axiosInstance';

export const PRIVILEGES = {
  ADMIN_BLOCK: 'adminBlock',
  INTEGRATION_PAGE: 'integrationPage',
  SYNC_OPTION: 'syncOption',
  CAPACITY_TABLE: 'capacityTable',
  CXO_METRIC_CONTRIBUTION: 'cxoMetricContribution',
  CXO_RELEASE_READINESS: 'cxoReleaseReadiness',
  CXO_ENGINEERING_SCORE: 'cxoEngineeringScore',
  CXO_RELEASE_VELOCITY: 'cxoReleaseVelocity',
  ENG_METRICS: 'engMetrics',
  RELEASE_DASHBOARD: 'releaseDashboard',
  STANDUP: 'standup',
  BILLING_RATE: 'billingRate',
  INVESTMENT_PROFILE: 'investmentProfile',
  TECH_QUALITY: 'techQuality',
  INTEGRATION_SETUP: 'integrationSetup',
  ROLE_MANAGEMENT: 'roleManagement',
  MULTI_PROJECT: 'multiProject',
};

export const PRIVILEGE_LABELS = {
  [PRIVILEGES.ADMIN_BLOCK]: 'Admin Block',
  [PRIVILEGES.INTEGRATION_PAGE]: 'Integration Page',
  [PRIVILEGES.SYNC_OPTION]: 'Sync Option',
  [PRIVILEGES.CAPACITY_TABLE]: 'Capacity Table',
  [PRIVILEGES.CXO_METRIC_CONTRIBUTION]: 'CXO Board Metric Contribution',
  [PRIVILEGES.CXO_RELEASE_READINESS]: 'CXO Widget: Release Readiness',
  [PRIVILEGES.CXO_ENGINEERING_SCORE]: 'CXO Widget: Engineering Score',
  [PRIVILEGES.CXO_RELEASE_VELOCITY]: 'CXO Widget: Release Velocity',
  [PRIVILEGES.ENG_METRICS]: 'Eng Metrics Page',
  [PRIVILEGES.RELEASE_DASHBOARD]: 'Release Dashboard',
  [PRIVILEGES.STANDUP]: 'Standup',
  [PRIVILEGES.BILLING_RATE]: 'Billing Rate / Total Billing Rate',
  [PRIVILEGES.INVESTMENT_PROFILE]: 'Investment Profile',
  [PRIVILEGES.TECH_QUALITY]: 'Tech Quality',
  [PRIVILEGES.INTEGRATION_SETUP]: 'Integration (Roles, Rate Card, Holidays)',
  [PRIVILEGES.ROLE_MANAGEMENT]: 'Role & Permission Management',
  [PRIVILEGES.MULTI_PROJECT]: 'Multi Project',
};

export const getUserRole = () => {
  try {
    // Prefer scoped mapping role from the last permissions refresh (project switch).
    // Header/profile updates must not leave us stuck on a stale global Admin role.
    const scopeRaw = sessionStorage.getItem('userPermissionsScope');
    if (scopeRaw) {
      const scope = JSON.parse(scopeRaw);
      if (scope?.role) return scope.role;
    }
    const raw = sessionStorage.getItem('userData');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.role || parsed?.data?.role || '';
    }
  } catch {
    /* ignore */
  }
  return '';
};

export const DEFAULT_ROLE_PRIVILEGES = {
  'super admin': Object.values(PRIVILEGES),
  admin: [
    PRIVILEGES.ADMIN_BLOCK,
    PRIVILEGES.INTEGRATION_PAGE,
    PRIVILEGES.SYNC_OPTION,
    PRIVILEGES.CAPACITY_TABLE,
    PRIVILEGES.CXO_METRIC_CONTRIBUTION,
    PRIVILEGES.CXO_RELEASE_READINESS,
    PRIVILEGES.CXO_ENGINEERING_SCORE,
    PRIVILEGES.CXO_RELEASE_VELOCITY,
    PRIVILEGES.ENG_METRICS,
    PRIVILEGES.RELEASE_DASHBOARD,
    PRIVILEGES.STANDUP,
    PRIVILEGES.BILLING_RATE,
    PRIVILEGES.INVESTMENT_PROFILE,
    PRIVILEGES.TECH_QUALITY,
    PRIVILEGES.MULTI_PROJECT,
    PRIVILEGES.ROLE_MANAGEMENT,
  ],
  manager: [
    PRIVILEGES.ADMIN_BLOCK,
    PRIVILEGES.INTEGRATION_PAGE,
    PRIVILEGES.SYNC_OPTION,
    PRIVILEGES.CAPACITY_TABLE,
    PRIVILEGES.CXO_METRIC_CONTRIBUTION,
    PRIVILEGES.CXO_RELEASE_READINESS,
    PRIVILEGES.CXO_ENGINEERING_SCORE,
    PRIVILEGES.CXO_RELEASE_VELOCITY,
    PRIVILEGES.ENG_METRICS,
    PRIVILEGES.RELEASE_DASHBOARD,
    PRIVILEGES.STANDUP,
    PRIVILEGES.BILLING_RATE,
    PRIVILEGES.INVESTMENT_PROFILE,
    PRIVILEGES.TECH_QUALITY,
    PRIVILEGES.MULTI_PROJECT,
    PRIVILEGES.ROLE_MANAGEMENT,
  ],
  'vice president': [
    PRIVILEGES.ADMIN_BLOCK,
    PRIVILEGES.INTEGRATION_PAGE,
    PRIVILEGES.SYNC_OPTION,
    PRIVILEGES.CAPACITY_TABLE,
    PRIVILEGES.CXO_METRIC_CONTRIBUTION,
    PRIVILEGES.CXO_RELEASE_READINESS,
    PRIVILEGES.CXO_ENGINEERING_SCORE,
    PRIVILEGES.CXO_RELEASE_VELOCITY,
    PRIVILEGES.ENG_METRICS,
    PRIVILEGES.RELEASE_DASHBOARD,
    PRIVILEGES.STANDUP,
    PRIVILEGES.BILLING_RATE,
    PRIVILEGES.INVESTMENT_PROFILE,
    PRIVILEGES.TECH_QUALITY,
    PRIVILEGES.MULTI_PROJECT,
    PRIVILEGES.ROLE_MANAGEMENT,
  ],
  'scrum master': [
    PRIVILEGES.CAPACITY_TABLE,
    PRIVILEGES.CXO_METRIC_CONTRIBUTION,
    PRIVILEGES.CXO_RELEASE_READINESS,
    PRIVILEGES.CXO_ENGINEERING_SCORE,
    PRIVILEGES.CXO_RELEASE_VELOCITY,
    PRIVILEGES.ENG_METRICS,
    PRIVILEGES.RELEASE_DASHBOARD,
    PRIVILEGES.STANDUP,
  ],
  employee: [PRIVILEGES.STANDUP],
};

const normalizeRoleKey = (role) =>
  String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

/** True once we have applied a scoped permissions payload (even if privileges are []). */
const hasScopedPermissionsCache = () => sessionStorage.getItem('userPermissions') !== null;

export const getEffectivePrivileges = () => {
  // Prefer scoped cache — including empty [] — so Employee on project A does not
  // fall back to Scrum Master defaults from userData.role after a project switch.
  if (hasScopedPermissionsCache()) {
    return getStoredPermissions();
  }
  const roleKey = normalizeRoleKey(getUserRole());
  return DEFAULT_ROLE_PRIVILEGES[roleKey] || [];
};

export const getStoredPermissions = () => {
  try {
    const raw = sessionStorage.getItem('userPermissions');
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : parsed?.privileges || [];
    }
  } catch {
    /* ignore */
  }
  return [];
};

export const setStoredPermissions = (permissionsPayload) => {
  const privileges = Array.isArray(permissionsPayload?.privileges)
    ? permissionsPayload.privileges
    : Array.isArray(permissionsPayload)
      ? permissionsPayload
      : [];
  sessionStorage.setItem('userPermissions', JSON.stringify(privileges));
  if (permissionsPayload?.companyId != null || permissionsPayload?.projectId != null) {
    sessionStorage.setItem(
      'userPermissionsScope',
      JSON.stringify({
        companyId: permissionsPayload.companyId ? String(permissionsPayload.companyId) : null,
        projectId: permissionsPayload.projectId ? String(permissionsPayload.projectId) : null,
        role: permissionsPayload.role || null,
      }),
    );
  }
  // Keep userData.role in sync with the active project mapping role
  const role = permissionsPayload?.role;
  if (role) {
    try {
      const raw = sessionStorage.getItem('userData');
      const parsed = raw ? JSON.parse(raw) : {};
      if (parsed.role !== role) {
        sessionStorage.setItem('userData', JSON.stringify({ ...parsed, role }));
      }
    } catch {
      /* ignore */
    }
  }
  try {
    window.dispatchEvent(new CustomEvent('qmetrix-permissions-updated'));
  } catch {
    /* ignore */
  }
};

/** Monotonic id so slower in-flight requests cannot overwrite a newer project scope */
let permissionsRefreshSeq = 0;

/** Load privileges for the currently selected org/project (per-project role in same org). */
export const refreshScopedPermissions = async (companyId, projectId) => {
  const seq = ++permissionsRefreshSeq;
  const cid = companyId || sessionStorage.getItem('companyId') || sessionStorage.getItem('orgId');
  // Explicit null = company-only (e.g. after org switch clears project)
  const pid =
    projectId === null
      ? undefined
      : projectId !== undefined && projectId !== ''
        ? String(projectId)
        : sessionStorage.getItem('projectId') || undefined;
  const params = {};
  if (cid) params.companyId = String(cid);
  if (pid) params.projectId = String(pid);

  const response = await axiosInstance.get('/api/admin/permissions', { params });
  const payload = response?.data?.data || response?.data;

  // Drop stale responses (common when switching LMS ↔ QMetry in the same org)
  if (seq !== permissionsRefreshSeq) {
    return null;
  }

  // Prefer API scope on the payload; fall back to session (may move during parallel switches)
  const payloadPid = payload?.projectId != null ? String(payload.projectId) : '';
  const sessionPid = sessionStorage.getItem('projectId') || '';
  if (pid && payloadPid && payloadPid !== String(pid)) {
    return null;
  }
  if (pid && !payloadPid && sessionPid && String(pid) !== String(sessionPid)) {
    return null;
  }
  const sessionCid =
    sessionStorage.getItem('companyId') || sessionStorage.getItem('orgId') || '';
  if (cid && sessionCid && String(cid) !== String(sessionCid)) {
    return null;
  }

  if (payload) setStoredPermissions(payload);
  return payload;
};

export const hasPrivilege = (key) => {
  if (normalizeRoleKey(getUserRole()) === 'super admin') return true;
  return getEffectivePrivileges().includes(key);
};

const CXO_WIDGET_PRIVILEGES = [
  PRIVILEGES.CXO_RELEASE_READINESS,
  PRIVILEGES.CXO_ENGINEERING_SCORE,
  PRIVILEGES.CXO_RELEASE_VELOCITY,
];

/**
 * Show a CXO metric widget when the role has that widget privilege.
 * Legacy: if CXO board is allowed but no widget privileges are assigned yet,
 * keep all three visible until Roles are updated.
 */
export const canViewCxoWidget = (widgetPrivilege) => {
  if (hasPrivilege(widgetPrivilege)) return true;
  const effective = getEffectivePrivileges();
  const hasAnyWidgetPrivilege = CXO_WIDGET_PRIVILEGES.some((k) => effective.includes(k));
  if (!hasAnyWidgetPrivilege && hasPrivilege(PRIVILEGES.CXO_METRIC_CONTRIBUTION)) {
    return true;
  }
  return false;
};

/**
 * QMetry360 dashboard access: full board privilege OR any individual CXO widget.
 * Widget-only roles must NOT unlock Eng Metrics / Release menus.
 */
export const hasCxoBoardAccess = () =>
  hasPrivilege(PRIVILEGES.CXO_METRIC_CONTRIBUTION) ||
  CXO_WIDGET_PRIVILEGES.some((k) => hasPrivilege(k));

/** Eng Metrics sidebar/page — dedicated privilege only (not CXO Board Metric Contribution). */
export const hasEngMetricsAccess = () => hasPrivilege(PRIVILEGES.ENG_METRICS);

/** Release Dashboard tab — dedicated privilege only. */
export const hasReleaseDashboardTabAccess = () => hasPrivilege(PRIVILEGES.RELEASE_DASHBOARD);

/** Investment Profile tab under Release — dedicated privilege only. */
export const hasInvestmentProfileAccess = () => hasPrivilege(PRIVILEGES.INVESTMENT_PROFILE);

/**
 * Release sidebar icon /route — show when user has Release Dashboard and/or Investment Profile.
 */
export const hasReleaseSectionAccess = () =>
  hasReleaseDashboardTabAccess() || hasInvestmentProfileAccess();

/** @deprecated use hasReleaseSectionAccess — kept for older imports */
export const hasReleaseDashboardAccess = hasReleaseSectionAccess;

export const meetsPrivilegeRequirement = (required) => {
  if (!required) return true;
  return hasPrivilege(required);
};

export const isSuperAdmin = () => normalizeRoleKey(getUserRole()) === 'super admin';

export const isAdmin = () => normalizeRoleKey(getUserRole()) === 'admin';

/** Admin or Super Admin — can edit assigned user roles on Project Access */
export const canEditProjectUserRoles = () =>
  isSuperAdmin() || isAdmin() || hasPrivilege(PRIVILEGES.ADMIN_BLOCK);

export const isEmployee = () => normalizeRoleKey(getUserRole()) === 'employee';

/** Map sidebar menu title → required privilege */
export const MENU_PRIVILEGE_MAP = {
  QMetry360: PRIVILEGES.CXO_METRIC_CONTRIBUTION,
  'Eng Metrics': PRIVILEGES.ENG_METRICS,
  Standup: PRIVILEGES.STANDUP,
  'Tech Quality': PRIVILEGES.TECH_QUALITY,
  Release: PRIVILEGES.RELEASE_DASHBOARD,
  Integration: PRIVILEGES.INTEGRATION_PAGE,
  Admin: PRIVILEGES.ADMIN_BLOCK,
  Roles: PRIVILEGES.ADMIN_BLOCK,
};

export const ROUTE_PRIVILEGE_MAP = {
  '/dashboard': PRIVILEGES.CXO_METRIC_CONTRIBUTION,
  '/jiraDashboard': PRIVILEGES.ENG_METRICS,
  '/gitDashboard': PRIVILEGES.ENG_METRICS,
  '/standUp': PRIVILEGES.STANDUP,
  '/techQuality': PRIVILEGES.TECH_QUALITY,
  '/release': PRIVILEGES.RELEASE_DASHBOARD,
  '/integration': PRIVILEGES.INTEGRATION_PAGE,
  '/capacityPlanning': PRIVILEGES.CAPACITY_TABLE,
  '/Roles&Billing': PRIVILEGES.INTEGRATION_SETUP,
  '/HolidayList': PRIVILEGES.INTEGRATION_SETUP,
  '/admin/project-access': PRIVILEGES.ADMIN_BLOCK,
  '/admin/roles': PRIVILEGES.ADMIN_BLOCK,
  '/admin/workspaces': PRIVILEGES.ADMIN_BLOCK,
  '/multiProjectSelection': PRIVILEGES.MULTI_PROJECT,
};

/** Prefer Standup, then first route the current privileges allow */
export const getDefaultAllowedPath = () => {
  const preferred = [
    '/standUp',
    '/dashboard',
    '/jiraDashboard',
    '/release',
    '/techQuality',
    '/integration',
  ];
  for (const path of preferred) {
    if (canAccessPath(path)) {
      return path;
    }
  }
  return '/standUp';
};

/** True when the current path is allowed for the active project role */
export const canAccessPath = (path) => {
  if (path === '/dashboard') return hasCxoBoardAccess();
  if (path === '/jiraDashboard' || path === '/gitDashboard') return hasEngMetricsAccess();
  if (path === '/release') return hasReleaseSectionAccess();
  const required = ROUTE_PRIVILEGE_MAP[path];
  if (!required) return true;
  if (isEmployee() && (required === PRIVILEGES.ADMIN_BLOCK || required === PRIVILEGES.ROLE_MANAGEMENT)) {
    return false;
  }
  return meetsPrivilegeRequirement(required);
};

/** Sidebar menu visibility — QMetry360 allows widget-only; Eng Metrics/Release need their own privileges */
export const canAccessMenu = (menuTitle) => {
  if (menuTitle === 'QMetry360') return hasCxoBoardAccess();
  if (menuTitle === 'Eng Metrics') return hasEngMetricsAccess();
  if (menuTitle === 'Release') return hasReleaseSectionAccess();
  if (menuTitle === 'Admin' || menuTitle === 'Roles') {
    return hasPrivilege(PRIVILEGES.ADMIN_BLOCK) && !isEmployee();
  }
  return meetsPrivilegeRequirement(MENU_PRIVILEGE_MAP[menuTitle]);
};
